import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { useMutation, useManualQuery } from "graphql-hooks";
import { Link as RouterLink } from "react-router-dom";

import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  MenuItem,
  Avatar,
  TextField,
  Tooltip,
  TablePagination,
  TableSortLabel,
  Link,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import AlertPopup from "./components/AlertPopup";
import FloatingMenu from "./components/FloatingMenu";
import EmptyState from "./components/EmptyState";
import {
  AssignmentTurnedInOutlined,
  FactCheckOutlined,
  VisibilityOutlined,
  EditOutlined,
  DeleteOutlined,
  ArrowUpward,
  ArrowDownward,
  ArrowDropDown,
  ArrowDropUp,
  AutoStoriesOutlined,
} from "@mui/icons-material";
import dayjs from "dayjs";


import { STORIES_BY_USER_UUID, UPDATE_STORY_STATUS, DELETE_STORY } from "./graphQL/story_qry";


import useIsTablet from "./hooks/useIsTablet";
import useIsMobile from "./hooks/useIsMobile";

// Status Options
const STATUS_OPTIONS = ["All", "Published", "Draft"];

//Tooltip Component
const StyledTooltip = ({ title, children }) => (
  <Tooltip
    title={title}
    placement="top"
    arrow
    PopperProps={{
      modifiers: [
        {
          name: "offset",
          options: {
            offset: [0, -5],
          },
        },
      ],
    }}
    componentsProps={{
      tooltip: {
        sx: {
          bgcolor: "#666",
          color: "white",
          fontSize: "12px",
          fontWeight: "500",
          borderRadius: "4px",
          padding: "6px 8px",
          "& .MuiTooltip-arrow": {
            color: "#666",
          },
        },
      },
    }}
  >
    {children}
  </Tooltip>
);

const MyStories = () => {

  const navigate = useNavigate();
  const isTablet = useIsTablet(1360);
  const isMobile = useIsMobile(833);
  const [statusFilter, setStatusFilter] = useState("All");
  const [mobileSortBy, setMobileSortBy] = useState("publishedOn");
  const [sortPanelOpen, setSortPanelOpen] = useState(false);
  const [orderBy, setOrderBy] = useState("publishedOn");
  const [order, setOrder] = useState("desc");
  const [page, setPage] = useState(0);
  const [stories, setStories] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(isMobile ? 5 : 10);
  const [floatMenuLeftMargin, setFloatMenuLeftMargin] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);

  let userUuid = localStorage.getItem("userUuid") ? localStorage.getItem("userUuid") : null;

  // Empty state component
  const renderEmptyState = () => (
    <EmptyState 
      type="stories"
      title="No Stories Available"
      description="No stories are available right now. Please check back later."
    />
  );

  const [getStoriesData] = useManualQuery(STORIES_BY_USER_UUID, {
    fetchPolicy: "network-only",
  });

  const [updateStoryStatus] = useMutation(UPDATE_STORY_STATUS, {
    fetchPolicy: "network-only",
  });

  const [deleteStory] = useMutation(DELETE_STORY, {
    fetchPolicy: "network-only",
  });

  const StoryImage = ({ src, alt, size = 80 }) => {
  if (!src) {
    return (
      <Box
        sx={{
          width: size,
          height: size,
          border: "1px solid #ccc",
          borderRadius: "8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#fafafa",
        }}
      >
        <Typography
          sx={{
            fontSize: "12px",
            color: "#999",
            textAlign: "center",
          }}
        >
          No image
        </Typography>
      </Box>
    );
  }

  return (
    <Avatar
      src={src}
      alt={alt}
      title={alt}
      sx={{
        width: size,
        height: size,
        borderRadius: "8px",
      }}
    />
  );
};


  // Alert popup state
  const [alertPopup, setAlertPopup] = useState({
    open: false,
    type: "",
    storyUuid: null,
  });

  useEffect(() => {
    if (isMobile && rowsPerPage === 10) setRowsPerPage(5);
    else if (!isMobile && rowsPerPage === 5) setRowsPerPage(10);
  }, [rowsPerPage, isMobile, isTablet]);

  // Handle click outside to close sort panel in Mobile
  useEffect(() => {
    const handleClickOutside = (event) => {
      const sortPanel = document.querySelector("[data-sort-panel]");
      if (sortPanel && !sortPanel.contains(event.target) && sortPanelOpen) {
        setSortPanelOpen(false);
      }
    };

    if (sortPanelOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [sortPanelOpen]);

  // Set floating menu place based on screen size
  useEffect(() => {
    const handleResize = () => {
      setFloatMenuLeftMargin(window.innerWidth - 100);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
  }, []);

  const setStoryList = async () => {
    if (!userUuid) return;
    const {
      loading: loadingStoryListData,
      error: errorStoryListData,
      data: storyListData,
    } = await getStoriesData({
      variables: {
        userUuid: userUuid,
      },
    });
    if (!loadingStoryListData && storyListData && storyListData.storiesByUseruuid) {

        setStories(
          storyListData.storiesByUseruuid.map(story => ({
            id: story.storyId,
            uuid: story.uuid,
            image: story.fileName,
            title: story.title,
            content: story.content,
            createdOn: dayjs(story.createDate).format("MMM D, YYYY"),
            publishedOn: story.isPublished ? dayjs(story.publishedDate).format("MMM D, YYYY") : "",
            status: story.isPublished ? "Published" : "Draft",
            //photoName: story.fileName?.split("/").pop(),
            //photoSize: story.fileSize ? (story.fileSize/ 1024).toFixed(0): ""
          }))
        );
      setIsLoaded(true);

    } else {
      console.log("errorCurrStoryData: ", errorStoryListData);

    }
  };

  useEffect(() => {
    (async () => setStoryList())();
  }, [userUuid]);


  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value));
    setPage(0);
  };

  // Filter change handlers
  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const handleMobileSortChange = (event) => {
    const sortValue = event.target.value;
    setMobileSortBy(sortValue);
    handleRequestSort(sortValue);
    setPage(0);
  };

  // Popup handlers


  const handlePublishClick = (storyUuid) => {
    setAlertPopup({
      open: true,
      type: "publish",
      storyUuid: storyUuid,
    });
  };

  const handleUnpublishClick = (storyUuid) => {
    setAlertPopup({
      open: true,
      type: "unpublish",
      storyUuid: storyUuid,
    });
  };

  const handleDeleteClick = (storyUuid) => {
    setAlertPopup({
      open: true,
      type: "delete",
      storyUuid: storyUuid,
    });
  };

  const handleConfirmAction = async() => {

    if (alertPopup.type === "delete")
    {
      const {
              loading: loadingStoryDelete,
              error: errorStoryDelete,
              data: storyDelete,
            } = await deleteStory({
                variables: {
                  storyUuid: alertPopup.storyUuid,
                },
              });
      if (!loadingStoryDelete && storyDelete && storyDelete.deleteStory) {
         setStories((prevStories) =>
         prevStories.filter((story) => story.uuid !== alertPopup.storyUuid)
      );
      }
      else
      {
        console.log("errorStoryDelete: ", errorStoryDelete);
      }

    }

    else{

      let isPublished = false;

      if (alertPopup.type === "publish") {
        isPublished = true

      } else if (alertPopup.type === "unpublish") {
          isPublished = false
      }

      const {
              loading: loadingStoryStatus,
              error: errorStoryStatus,
              data: storyStatus,
            } = await updateStoryStatus({
                variables: {
                  storyUuid: alertPopup.storyUuid,
                  isPublished: isPublished
                },
              });
      if (!loadingStoryStatus && storyStatus && storyStatus.updateStoryStatus) {
            setStories((prevStories) =>
            prevStories.map((story) =>
              story.uuid === alertPopup.storyUuid
                ? {
                    ...story,
                    publishedOn: isPublished ? (new Date().toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })) : "",
                    status: isPublished ? "Published": "Draft",
                  }
                : story
            )
          );
      }
      else
      {
        console.log("errorStoryData: ", errorStoryStatus);
      }

    }

    setAlertPopup({ open: false, type: "", storyUuid: null });
  };

  const handleCancelAction = () => {
    setAlertPopup({ open: false, type: "", storyUuid: null });
  };

  // Sorting handler
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    setMobileSortBy(property);
  };

  // Filter and sort data
  const getFilteredData = () => {
    let filteredData = stories;

    if (statusFilter !== "All") {
      filteredData = filteredData.filter(
        (story) => story.status === statusFilter
      );
    }

    if (orderBy) {
      filteredData.sort((a, b) => {
        let aValue = a[orderBy];
        let bValue = b[orderBy];

        if (orderBy === "title") {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (orderBy === "createdOn" || orderBy === "publishedOn") {
          aValue = aValue ? new Date(aValue) : new Date(0);
          bValue = bValue ? new Date(bValue) : new Date(0);
        }

        if (orderBy === "status") {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (aValue < bValue) {
          return order === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return order === "asc" ? 1 : -1;
        }
        return 0;
      });
    }

    return filteredData;
  };

  const filteredData = getFilteredData();
  const paginatedData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <>
      <Helmet>
        <title>R3PLAY - My Stories</title>
        <meta name="description" content="Manage and edit your stories" />
      </Helmet>

      {/* Mobile View - Only shows on screens < 834px */}
      <Box
        sx={{
          backgroundColor: "#f9f9f9",
          minHeight: "100vh",
          py: 4,
          px: "16px",
          display: "none",
          "@media (max-width: 833px)": {
            display: "block",
          },
        }}
      >
        {/* Mobile Header */}
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: "8px",
            p: 2,
            mb: 2,
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
        >
          <Typography
            sx={{
              fontSize: "24px",
              fontWeight: "bold",
              color: "#333",
              mb: 1,
            }}
          >
            My Stories
          </Typography>
          <Typography
            sx={{
              fontSize: "14px",
              color: "#666",
              mb: 2,
            }}
          >
            Manage and edit your stories
          </Typography>

          {/* Mobile Filter Controls */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexDirection: "row",
            }}
          >
            <TextField
              select
              label="Status"
              value={statusFilter}
              onChange={handleStatusFilterChange}
              sx={{
                width: "50%",
                "& .MuiOutlinedInput-root": {
                  height: "40px",
                  backgroundColor: "white",
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e0e0e0",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e0e0e0",
                    borderWidth: "1px",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "#666",
                  fontSize: "14px",
                  "&.Mui-focused": {
                    color: "#666",
                  },
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#e0e0e0",
                },
              }}
              SelectProps={{
                IconComponent: ArrowDropDown,
              }}
            >
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Box>

        {!isLoaded ? (
            <Box p={4} textAlign="center">Loading...</Box>
          )
          : isLoaded && stories.length === 0 ?
          (
            renderEmptyState()
          ) : (
            <>

          {/* Mobile Sort Panel */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mb: 2,
              width: "100%",
            }}
          >
            <Box sx={{ width: "230px", position: "relative" }} data-sort-panel>
              <TextField
                label="Sort by"
                value={
                  mobileSortBy
                    ? (() => {
                        const options = {
                          title: "Title",
                          createdOn: "Created on",
                          publishedOn: "Published on",
                          status: "Status",
                        };
                        return options[mobileSortBy] || "";
                      })()
                    : ""
                }
                onClick={() => setSortPanelOpen(!sortPanelOpen)}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      {mobileSortBy &&
                        (order === "desc" ? (
                          <ArrowDownward sx={{ fontSize: 20, color: "#666" }} />
                        ) : (
                          <ArrowUpward sx={{ fontSize: 20, color: "#666" }} />
                        ))}
                      {sortPanelOpen ? (
                        <ArrowDropUp sx={{ color: "#666" }} />
                      ) : (
                        <ArrowDropDown sx={{ color: "#666" }} />
                      )}
                    </Box>
                  ),
                }}
                sx={{
                  width: "230px",
                  cursor: "pointer",
                  "& .MuiOutlinedInput-root": {
                    height: "40px",
                    backgroundColor: "white",
                    cursor: "pointer",
                    paddingRight: "8px",
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#d0d0d0",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e0e0e0",
                      borderWidth: "1px",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "#666",
                    fontSize: "14px",
                    "&.Mui-focused": {
                      color: "#666",
                    },
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e0e0e0",
                    pr: 0,
                  },
                  "& .MuiOutlinedInput-input": {
                    cursor: "pointer",
                  },
                }}
              />

              {/* Sort options panel */}
              {sortPanelOpen && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "40px",
                    left: 0,
                    right: 0,
                    backgroundColor: "white",
                    border: "1px solid #e0e0e0",
                    borderTop: "none",
                    borderRadius: "0 0 4px 4px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    zIndex: 1000,
                  }}
                >
                  <RadioGroup
                    value={mobileSortBy}
                    onChange={(e) => {
                      setMobileSortBy(e.target.value);
                      setOrder("desc");
                      setOrderBy(e.target.value);
                      setPage(0);
                    }}
                  >
                    {[
                      { value: "title", label: "Title" },
                      { value: "createdOn", label: "Created on" },
                      { value: "publishedOn", label: "Published on" },
                    ].map((option) => (
                      <Box
                        key={option.value}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          px: 1,
                          pl: 0,
                          py: 0.5,
                          "&:hover": {
                            backgroundColor: "#f5f5f5",
                          },
                        }}
                      >
                        <FormControlLabel
                          value={option.value}
                          control={
                            <Radio
                              size="small"
                              sx={{
                                color: "#666",
                              }}
                            />
                          }
                          label={
                            <Typography sx={{ fontSize: "14px", color: "#333" }}>
                              {option.label}
                            </Typography>
                          }
                          sx={{ margin: 0, flex: 1 }}
                        />
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            width: "55px",
                            justifyContent: "space-between",
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setMobileSortBy(option.value);
                              setOrder("desc");
                              setOrderBy(option.value);
                              setPage(0);
                              setSortPanelOpen(false);
                            }}
                            sx={{
                              p: 0.25,
                              backgroundColor:
                                mobileSortBy === option.value && order === "desc"
                                  ? "#fce4ec"
                                  : "transparent",
                            }}
                          >
                            <ArrowDownward
                              fontSize="small"
                              sx={{ fontSize: 20, color: "#666" }}
                            />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setMobileSortBy(option.value);
                              setOrder("asc");
                              setOrderBy(option.value);
                              setPage(0);
                              setSortPanelOpen(false);
                            }}
                            sx={{
                              p: 0.25,
                              backgroundColor:
                                mobileSortBy === option.value && order === "asc"
                                  ? "#fce4ec"
                                  : "transparent",
                            }}
                          >
                            <ArrowUpward
                              fontSize="small"
                              sx={{ fontSize: 20, color: "#666" }}
                            />
                          </IconButton>
                        </Box>
                      </Box>
                    ))}
                  </RadioGroup>
                </Box>
              )}
            </Box>
          </Box>

          {/* Mobile Cards */}
          {paginatedData.map((story, index) => (
            <Box
              key={story.id}
              sx={{
                backgroundColor: index % 2 === 0 ? "#FDECF0" : "white",
                borderRadius: "8px",
                mb: 2,
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                overflow: "hidden",
              }}
            >
              <Table>
                <TableBody>
                  {/* Row 1: Image + Title */}
                  <TableRow sx={{ borderBottom: "1px solid #e0e0e0" }}>
                    <TableCell sx={{ py: 2, width: "125px", border: "none" }}>
                      <StoryImage
                        src={story.image}
                        alt={story.title}
                        size={80}
                      />

                    </TableCell>
                    <TableCell sx={{ py: 2, border: "none" }}>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#333",
                          fontWeight: "500",
                          lineHeight: 1.4,
                        }}
                      >
                        {story.title}
                      </Typography>
                    </TableCell>
                  </TableRow>

                  {/* Row 2: Created on + Published on */}
                  <TableRow sx={{ borderBottom: "1px solid #e0e0e0" }}>
                    <TableCell
                      sx={{
                        py: 2,
                        border: "none",
                        verticalAlign: "top",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#666",
                          fontWeight: "bold",
                          mb: 0.5,
                        }}
                      >
                        Created on
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#333",
                        }}
                      >
                        {story.createdOn}
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 2,
                        border: "none",
                        verticalAlign: "top",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#666",
                          fontWeight: "bold",
                          mb: 0.5,
                        }}
                      >
                        Published on
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#333",
                        }}
                      >
                        {story.publishedOn || "-"}
                      </Typography>
                    </TableCell>
                  </TableRow>

                  {/* Row 3: Status and Actions */}
                  <TableRow>
                    <TableCell
                      sx={{
                        pt: 2,
                        pb: 0,
                        border: "none",
                        verticalAlign: "top",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#666",
                          fontWeight: "bold",
                          mb: 0.5,
                        }}
                      >
                        Status
                      </Typography>
                      <Box
                        sx={{
                          backgroundColor: "white",
                          color: "#333",
                          border: "1px solid #000",
                          fontSize: "12px",
                          fontWeight: "500",
                          width: "100px",
                          height: "30px",
                          borderRadius: "4px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          textAlign: "center",
                        }}
                      >
                        {story.status}
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        pt: 2,
                        pb: 1,
                        border: "none",
                        verticalAlign: "middle",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          justifyContent: "flex-start",
                        }}
                      >
                        {story.publishedOn === "" ? (
                          <StyledTooltip title="Publish this story">
                            <IconButton
                              size="small"
                              onClick={() => handlePublishClick(story.uuid)}
                              sx={{
                                color: "#666",
                                "&:hover": {
                                  backgroundColor: "rgba(0,0,0,0.04)",
                                },
                              }}
                            >
                              <AssignmentTurnedInOutlined fontSize="small" />
                            </IconButton>
                          </StyledTooltip>
                        ) : (
                          <StyledTooltip title="Unpublish this story">
                            <IconButton
                              size="small"
                              onClick={() => handleUnpublishClick(story.uuid)}
                              sx={{
                                color: "#666",
                                "&:hover": {
                                  backgroundColor: "rgba(0,0,0,0.04)",
                                },
                              }}
                            >
                              <FactCheckOutlined fontSize="small" />
                            </IconButton>
                          </StyledTooltip>
                        )}
                        <StyledTooltip title="View this story">
                          <IconButton
                            size="small"
                            sx={{
                              color: "#666",
                              "&:hover": {
                                backgroundColor: "rgba(0,0,0,0.04)",
                              },
                            }}
                          >
                            <Link
                              component={RouterLink}
                              to={`/story/${story.uuid}`}
                              target="_blank"
                              color="black"
                              underline="none"
                              sx={{
                                color: "#666",
                                "&:hover": {
                                  backgroundColor: "rgba(0,0,0,0.04)",
                                },
                              }}
                            >
                              <VisibilityOutlined fontSize="small" />
                            </Link>
                          </IconButton>
                        </StyledTooltip>
                        <StyledTooltip title="Edit this story">
                          <IconButton
                            size="small"
                            onClick={() =>
                              navigate(`/story/${story.uuid}/edit`, {
                                state: { story }
                              })
                            }
                            sx={{
                              color: "#666",
                              "&:hover": {
                                backgroundColor: "rgba(0,0,0,0.04)",
                              },
                            }}
                          >
                            <EditOutlined fontSize="small" />
                          </IconButton>
                        </StyledTooltip>
                        <StyledTooltip title="Delete this story">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(story.uuid)}
                            sx={{
                              color: "#666",
                              "&:hover": {
                                backgroundColor: "rgba(0,0,0,0.04)",
                              },
                            }}
                          >
                            <DeleteOutlined fontSize="small" />
                          </IconButton>
                        </StyledTooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              {/* Card bottom padding */}
              <Box sx={{ pb: 2 }} />
            </Box>
          ))}

          {/* Mobile Pagination */}
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: "8px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}
          >
            <TablePagination
              rowsPerPageOptions={[5, 15, 20]}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                "& .MuiTablePagination-toolbar": {
                  paddingLeft: 1,
                  paddingRight: 1,
                  minHeight: "52px",
                },
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                  fontSize: "14px",
                  color: "#666",
                },
                "& .MuiTablePagination-select": {
                  fontSize: "14px",
                },
                "& .MuiTablePagination-actions": {
                  "& .MuiIconButton-root": {
                    padding: "6px",
                  },
                },
              }}
            />
          </Box>
        </>
        )}
      </Box>

      {/* Desktop/Tablet View - Only shows on screens >= 834px */}
      <Box
        sx={{
          backgroundColor: "#f9f9f9",
          minHeight: "100vh",
          py: 4,
          px: "100px",
          "@media (max-width: 1360px)": {
            px: "16px",
          },
          "@media (max-width: 833px)": {
            display: "none",
          },
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: "8px",
            height: "91px",
            p: 1,
            pl: 2,
            pr: 2,
            mb: 2,
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {/* Title Section */}
            <Box>
              <Typography
                sx={{
                  fontSize: "30px",
                  fontWeight: "bold",
                  color: "#333",
                }}
              >
                My Stories
              </Typography>
              <Typography
                sx={{
                  fontSize: "16px",
                  color: "#666",
                }}
              >
                Manage and edit your stories
              </Typography>
            </Box>

            {/* Filter Controls */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
              }}
            >
              <TextField
                select
                label="Status"
                value={statusFilter}
                onChange={handleStatusFilterChange}
                sx={{
                  width: "150px",
                  "& .MuiOutlinedInput-root": {
                    height: "40px",
                    backgroundColor: "white",
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e0e0e0",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e0e0e0",
                      borderWidth: "1px",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "#666",
                    fontSize: "14px",
                    "&.Mui-focused": {
                      color: "#666",
                    },
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e0e0e0",
                  },
                }}
                SelectProps={{
                  IconComponent: ArrowDropDown,
                }}
              >
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>
        </Box>

        {!isLoaded ? (
            <Box p={4} textAlign="center">Loading...</Box>
          )
          : isLoaded && stories.length === 0 ?
          (
            renderEmptyState()
          ) : (
            <>

          {/* Table */}
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              borderRadius: "8px",
              overflow: "hidden",
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "white" }}>
                  <TableCell sx={{ fontWeight: "600", color: "#333", py: 2 }}>
                    Image
                  </TableCell>
                  <TableCell sx={{ fontWeight: "600", color: "#333", py: 2 }}>
                    <TableSortLabel
                      active={orderBy === "title"}
                      direction={orderBy === "title" ? order : "asc"}
                      onClick={() => handleRequestSort("title")}
                      sx={{
                        fontWeight: "600",
                        color: "#333",
                        "&.Mui-active": {
                          color: "#333",
                        },
                        "&:hover": {
                          color: "#333",
                        },
                        "& .MuiTableSortLabel-icon": {
                          color: "#333 !important",
                        },
                      }}
                    >
                      Title
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: "600", color: "#333", py: 2 }}>
                    <TableSortLabel
                      active={orderBy === "createdOn"}
                      direction={orderBy === "createdOn" ? order : "asc"}
                      onClick={() => handleRequestSort("createdOn")}
                      sx={{
                        fontWeight: "600",
                        color: "#333",
                        "&.Mui-active": {
                          color: "#333",
                        },
                        "&:hover": {
                          color: "#333",
                        },
                        "& .MuiTableSortLabel-icon": {
                          color: "#333 !important",
                        },
                      }}
                    >
                      Created on
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: "600", color: "#333", py: 2 }}>
                    <TableSortLabel
                      active={orderBy === "publishedOn"}
                      direction={orderBy === "publishedOn" ? order : "asc"}
                      onClick={() => handleRequestSort("publishedOn")}
                      sx={{
                        fontWeight: "600",
                        color: "#333",
                        "&.Mui-active": {
                          color: "#333",
                        },
                        "&:hover": {
                          color: "#333",
                        },
                        "& .MuiTableSortLabel-icon": {
                          color: "#333 !important",
                        },
                      }}
                    >
                      Published on
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: "600", color: "#333", py: 2 }}>
                    <TableSortLabel
                      active={orderBy === "status"}
                      direction={orderBy === "status" ? order : "asc"}
                      onClick={() => handleRequestSort("status")}
                      sx={{
                        fontWeight: "600",
                        color: "#333",
                        "&.Mui-active": {
                          color: "#333",
                        },
                        "&:hover": {
                          color: "#333",
                        },
                        "& .MuiTableSortLabel-icon": {
                          color: "#333 !important",
                        },
                      }}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "600",
                      color: "#333",
                      py: 2,
                      minWidth: "150px",
                      textAlign: "center",
                    }}
                  >
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((story) => (
                  <React.Fragment key={story.id}>
                    <TableRow
                      sx={{
                        "&:hover": {
                          backgroundColor: "#f9f9f9",
                        },
                        ...(isTablet && {
                          "& td": {
                            borderBottom: "none",
                          },
                        }),
                      }}
                    >
                      <TableCell sx={{ py: 2, pb: isTablet ? 0 : 2 }}>
                        <StoryImage
                          src={story.image}
                          alt={story.title}
                          size={isTablet ? 40 : 80}
                        />

                      </TableCell>

                      {/* Title Cell - Spans 6 columns on tablet, normal on desktop */}
                      <TableCell
                        colSpan={isTablet ? 6 : 1}
                        sx={{
                          py: isTablet ? 1 : 2,
                          maxWidth: "300px",
                          "@media (max-width: 1360px)": {
                            maxWidth: "none",
                            paddingBottom: 0,
                          },
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "14px",
                            color: "#333",
                            fontWeight: "500",
                            lineHeight: 1.4,
                          }}
                        >
                          {story.title}
                        </Typography>
                      </TableCell>

                      {/* Desktop columns - hidden on tablet */}
                      {!isTablet && (
                        <>
                          <TableCell sx={{ py: 2 }}>
                            <Typography
                              sx={{
                                fontSize: "14px",
                                color: "#333",
                              }}
                            >
                              {story.createdOn}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Typography
                              sx={{
                                fontSize: "14px",
                                color: "#333",
                              }}
                            >
                              {story.publishedOn || "-"}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Box
                              sx={{
                                backgroundColor: "white",
                                color: "#333",
                                border: "1px solid #000",
                                fontSize: "12px",
                                fontWeight: "500",
                                width: "100px",
                                height: "30px",
                                minWidth: "80px",
                                borderRadius: "4px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                textAlign: "center",
                              }}
                            >
                              {story.status}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                justifyContent: "center",
                              }}
                            >
                              {story.publishedOn === "" ? (
                                <StyledTooltip title="Publish this story">
                                  <IconButton
                                    size="small"
                                    onClick={() => handlePublishClick(story.uuid)}
                                    sx={{
                                      color: "#666",
                                      "&:hover": {
                                        backgroundColor: "rgba(0,0,0,0.04)",
                                      },
                                    }}
                                  >
                                    <AssignmentTurnedInOutlined fontSize="small" />
                                  </IconButton>
                                </StyledTooltip>
                              ) : (
                                <StyledTooltip title="Unpublish this story">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleUnpublishClick(story.uuid)}
                                    sx={{
                                      color: "#666",
                                      "&:hover": {
                                        backgroundColor: "rgba(0,0,0,0.04)",
                                      },
                                    }}
                                  >
                                    <FactCheckOutlined fontSize="small" />
                                  </IconButton>
                                </StyledTooltip>
                              )}
                              <StyledTooltip title="View this story">
                                <IconButton
                                  size="small"
                                  sx={{
                                    color: "#666",
                                    "&:hover": {
                                      backgroundColor: "rgba(0,0,0,0.04)",
                                    },
                                  }}
                                >
                                  <Link
                                    component={RouterLink}
                                    to={`/story/${story.uuid}`}
                                    target="_blank"
                                    color="black"
                                    underline="none"
                                    sx={{
                                      color: "#666",
                                      "&:hover": {
                                        backgroundColor: "rgba(0,0,0,0.04)",
                                      },
                                    }}
                                  >
                                    <VisibilityOutlined fontSize="small" />
                                  </Link>
                                </IconButton>
                              </StyledTooltip>
                              <StyledTooltip title="Edit this story">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    navigate(`/story/${story.uuid}/edit`, {
                                      state: { story }
                                    })
                                  }
                                  sx={{
                                    color: "#666",
                                    "&:hover": {
                                      backgroundColor: "rgba(0,0,0,0.04)",
                                    },
                                  }}
                                >
                                  <EditOutlined fontSize="small" />
                                </IconButton>
                              </StyledTooltip>
                              <StyledTooltip title="Delete this story">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteClick(story.uuid)}
                                  sx={{
                                    color: "#666",
                                    "&:hover": {
                                      backgroundColor: "rgba(0,0,0,0.04)",
                                    },
                                  }}
                                >
                                  <DeleteOutlined fontSize="small" />
                                </IconButton>
                              </StyledTooltip>
                            </Box>
                          </TableCell>
                        </>
                      )}
                    </TableRow>

                    {/* Second row for tablet view */}
                    {isTablet && (
                      <TableRow
                        key={`${story.id}-details`}
                        sx={{
                          "&:hover": {
                            backgroundColor: "#f9f9f9",
                          },
                          "& td": {
                            borderTop: "none",
                          },
                        }}
                      >
                        <TableCell sx={{ py: 0 }}></TableCell>
                        <TableCell sx={{ py: 0 }}></TableCell>

                        <TableCell sx={{ pt: 0, pb: 1 }}>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "#333",
                            }}
                          >
                            {story.createdOn}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ pt: 0, pb: 1 }}>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "#333",
                            }}
                          >
                            {story.publishedOn || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ pt: 0, pb: 2 }}>
                          <Box
                            sx={{
                              backgroundColor: "white",
                              color: "#333",
                              border: "1px solid #000",
                              fontSize: "12px",
                              fontWeight: "500",
                              width: "100px",
                              height: "30px",
                              minWidth: "80px",
                              borderRadius: "4px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              textAlign: "center",
                            }}
                          >
                            {story.status}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ pt: 0, pb: 1 }}>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              justifyContent: "center",
                            }}
                          >
                            {story.publishedOn === "" ? (
                              <StyledTooltip title="Publish this story">
                                <IconButton
                                  size="small"
                                  onClick={() => handlePublishClick(story.uuid)}
                                  sx={{
                                    color: "#666",
                                    "&:hover": {
                                      backgroundColor: "rgba(0,0,0,0.04)",
                                    },
                                  }}
                                >
                                  <AssignmentTurnedInOutlined fontSize="small" />
                                </IconButton>
                              </StyledTooltip>
                            ) : (
                              <StyledTooltip title="Unpublish this story">
                                <IconButton
                                  size="small"
                                  onClick={() => handleUnpublishClick(story.uuid)}
                                  sx={{
                                    color: "#666",
                                    "&:hover": {
                                      backgroundColor: "rgba(0,0,0,0.04)",
                                    },
                                  }}
                                >
                                  <FactCheckOutlined fontSize="small" />
                                </IconButton>
                              </StyledTooltip>
                            )}
                            <StyledTooltip title="View this story">
                              <IconButton
                                size="small"
                                sx={{
                                  color: "#666",
                                  "&:hover": {
                                    backgroundColor: "rgba(0,0,0,0.04)",
                                  },
                                }}
                              >
                                <Link
                                  component={RouterLink}
                                  to={`/story/${story.uuid}`}
                                  target="_blank"
                                  color="black"
                                  underline="none"
                                  sx={{
                                    color: "#666",
                                    "&:hover": {
                                      backgroundColor: "rgba(0,0,0,0.04)",
                                    },
                                  }}
                                >
                                  <VisibilityOutlined fontSize="small" />
                                </Link>
                              </IconButton>
                            </StyledTooltip>
                            <StyledTooltip title="Edit this story">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  navigate(`/story/${story.uuid}/edit`, {
                                    state: { story }
                                  })
                                }
                                sx={{
                                  color: "#666",
                                  "&:hover": {
                                    backgroundColor: "rgba(0,0,0,0.04)",
                                  },
                                }}
                              >
                                <EditOutlined fontSize="small" />
                              </IconButton>
                            </StyledTooltip>
                            <StyledTooltip title="Delete this story">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteClick(story.uuid)}
                                sx={{
                                  color: "#666",
                                  "&:hover": {
                                    backgroundColor: "rgba(0,0,0,0.04)",
                                  },
                                }}
                              >
                                <DeleteOutlined fontSize="small" />
                              </IconButton>
                            </StyledTooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[10, 15, 20]}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                borderTop: "1px solid #e0e0e0",
                "& .MuiTablePagination-toolbar": {
                  paddingLeft: 2,
                  paddingRight: 2,
                },
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                  fontSize: "14px",
                  color: "#666",
                },
                "& .MuiTablePagination-select": {
                  fontSize: "14px",
                },
              }}
            />
          </TableContainer>
        </>
      )}
      </Box>
      <FloatingMenu left={floatMenuLeftMargin} />

      {/* Alert Popup */}
      <AlertPopup
        open={alertPopup.open}
        onClose={handleCancelAction}
        title={
          alertPopup.type === "publish"
            ? "Draft to Publish"
            : alertPopup.type === "unpublish"
            ? "Publish to Draft"
            : "Delete Story"
        }
        message={
          alertPopup.type === "publish"
            ? "Are you ready to publish this story?"
            : alertPopup.type === "unpublish"
            ? "Are you sure you want to unpublish this story?"
            : "Are you sure you want to delete this story?"
        }
        confirmText={
          alertPopup.type === "publish"
            ? "YES, PUBLISH"
            : alertPopup.type === "unpublish"
            ? "YES, UNPUBLISH"
            : "YES, DELETE"
        }
        onConfirm={handleConfirmAction}
      />
    </>
  );
};

export default MyStories;
